/**
 * Next.js의 인증 설정 및 핸들러
 * - 인증 제공자 설정
 * - 인증 처리 로직
 * - 세션 관리
 */
import {
  REFRESH_TOKEN_EXPIRATION_TIME_MS,
  TOKEN_EXPIRATION_TIME_MS,
} from '@/features/auth/constants/tokenTime';
import logger from '@/utils/logger';
import { AxiosError } from 'axios';
import NextAuth, { CredentialsSignin } from 'next-auth';
import Credentials from 'next-auth/providers/credentials';
import { authService } from '../services/authService';
import { AuthProvider } from '../types/enum';

// 토큰 만료 시간 설정
const tokenExpiresAt = Date.now() + TOKEN_EXPIRATION_TIME_MS;
const refreshTokenExpiresAt = Date.now() + REFRESH_TOKEN_EXPIRATION_TIME_MS;

class InvalidSigninError extends CredentialsSignin {
  constructor(message: string, status?: number) {
    super(message, { cause: status });
    this.code = message;
  }
}

/**
 * Next.js 인증 설정
 * - Credentials 제공자 설정 (이메일, 소셜 로그인)
 * - 인증 처리 로직 구현
 * - 세션 및 토큰 관리
 */
export const { handlers, auth, signIn, signOut } = NextAuth({
  providers: [
    Credentials({
      // 인증에 필요한 자격 증명 필드 정의
      credentials: {
        provider: { type: 'text' }, // 인증 제공자 (email, google 등)
        signInId: { type: 'text' }, // 로그인 ID
        password: { type: 'password' }, // 비밀번호
        token: { type: 'text' }, // 소셜 로그인 토큰
      },
      /**
       * 인증 처리 로직
       * @param credentials - 이용자가 제공한 자격 증명
       * @returns 인증된 이용자 정보 또는 null
       */
      authorize: async (credentials) => {
        logger.info('>> authorize', credentials);

        if (!credentials?.provider)
          throw new InvalidSigninError('Provider is required');

        try {
          let result = null;

          // 이메일 로그인 처리
          if (credentials.provider === AuthProvider.EMAIL) {
            if (!credentials.password)
              throw new InvalidSigninError(
                'Password is required for email signin',
              );
            result = await authService.signInWithEmail(
              credentials.signInId as string,
              credentials.password as string,
            );
          }
          // 소셜 로그인 처리
          else {
            result = await authService.signInWithSocial(
              Object.values(AuthProvider).includes(
                credentials.provider as AuthProvider,
              )
                ? (credentials.provider as AuthProvider)
                : AuthProvider.NONE,
              credentials.signInId as string,
              credentials.token as string,
            );
          }

          // 인증 성공 시 이용자 정보 반환
          if (!result) throw new InvalidSigninError('로그인에 실패했습니다.');

          return {
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            id: result.user.id.toString(),
            provider: credentials.provider as AuthProvider,
            tier: result.user.tier,
            company_role: result.user.company_role,
            name: result.user.name,
            email: result.user.email,
          };
        } catch (error) {
          if (error instanceof AxiosError) {
            throw new InvalidSigninError(
              error.response?.data.message,
              error.response?.status,
            );
          }
          throw error;
        }
      },
    }),
  ],
  events: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async signOut(message: any) {
      logger.info('>> signOut', message);

      if ('token' in message && message.token?.refreshToken) {
        await authService.signOut(message.token.refreshToken);
      }
    },
  },
  callbacks: {
    async jwt({ token, user, trigger }) {
      logger.info('>> jwt');
      // logger.info("++ token", token);
      // logger.info("++ user", user);

      // 초기 로그인
      if (trigger === 'signIn' && user) {
        return {
          sub: user.id,
          accessToken: user.accessToken,
          refreshToken: user.refreshToken,
          provider: user.provider,
          accessTokenExpires: tokenExpiresAt,
          refreshTokenExpires: refreshTokenExpiresAt,
          tier: user.tier,
          company_role: user.company_role,
          name: user.name,
          email: user.email,
        };
      }

      // 토큰이 만료되지 않았다면 그대로 반환
      const tokenDate = new Date(token.accessTokenExpires as number);
      if (Date.now() < tokenDate.getTime()) {
        return token;
      }

      // 리프레시 토큰 체크
      if (!token.refreshToken) {
        logger.error('not found refreshToken');
        throw new Error('not found refreshToken'); // TODO check error result
      }
      const refreshTokenDate = new Date(token.refreshTokenExpires as number);
      if (Date.now() >= refreshTokenDate.getTime()) {
        logger.info('-- refreshToken is expired');
        throw new Error('refreshToken is expired'); // TODO check error result
      }

      // 토큰 갱신
      return authService
        .refreshToken(token.refreshToken as string)
        .then((result) => {
          logger.info('++ result', result);
          return {
            sub: token.sub,
            accessToken: result.access,
            refreshToken: token.refreshToken,
            provider: token.provider,
            accessTokenExpires: tokenExpiresAt,
            refreshTokenExpires: refreshTokenExpiresAt,
            tier: token.tier,
            company_role: token.company_role,
            name: token.name,
            email: token.email,
          };
        })
        .catch((error) => {
          logger.error('Token refresh error:', error);
          // throw new Error("Token refresh error"); // TODO check error result
          return null;
        });
    },
    async session({ session, token }) {
      if (session.user) {
        session.user.accessToken = token.accessToken as string;
        session.user.id = token.sub ?? '';
        session.user.provider = token.provider as AuthProvider;
        session.user.tier = token.tier as number;
        session.user.company_role = token.company_role as string;
        session.user.name = token.name as string;
        session.user.email = token.email as string;
      }
      return session;
    },
  },
});
